import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormHelperText,
  Paper,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  useGetNotificationGroupQuery,
  useCreateNotificationGroupMutation,
  useUpdateNotificationGroupMutation,
  usePreviewQueryFilterMutation,
} from '../../services/connectApi';
import type { NotificationGroup, User } from '../../models';
import UserAutocomplete from '../../components/UserAutocomplete';

// Helper function to determine value type based on field name
const getValueType = (field: string, value: any): 'string' | 'boolean' | 'number' | 'date' => {
  if (field.includes('is_') || field === 'active') {
    return 'boolean';
  }
  if (field.includes('date') || field.includes('_at') || field.includes('joined')) {
    return 'date';
  }
  if (typeof value === 'number' || field.includes('count') || field.includes('id')) {
    return 'number';
  }
  return 'string';
};

interface QueryFilterField {
  key: string;
  operator: string;
  value: string | boolean | number;
  valueType: 'string' | 'boolean' | 'number' | 'date';
}

const GroupForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [group, setGroup] = useState<Partial<NotificationGroup>>({
    name: '',
    description: '',
    users: [],
    query_filter: {},
  });
  
  const [queryFilters, setQueryFilters] = useState<QueryFilterField[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  const { data: existingGroup, isLoading } = useGetNotificationGroupQuery(
    id as string,
    { skip: !isEditMode }
  );
  
  const [createGroup] = useCreateNotificationGroupMutation();
  const [updateGroup] = useUpdateNotificationGroupMutation();
  const [previewQueryFilter] = usePreviewQueryFilterMutation();

  useEffect(() => {
    if (isEditMode && existingGroup) {
      setGroup(existingGroup);
      
      // Convert query filter object to array for UI
      const filters: QueryFilterField[] = [];
      Object.entries(existingGroup.query_filter || {}).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          Object.entries(value).forEach(([op, val]) => {
            filters.push({
              key,
              operator: op,
              value: val as string | number | boolean,
              valueType: getValueType(key, val),
            });
          });
        } else {
          filters.push({
            key,
            operator: 'eq',
            value: value,
            valueType: getValueType(key, value),
          });
        }
      });
      setQueryFilters(filters);
      
      // Set selected users if they are loaded as objects
      if (existingGroup.users && Array.isArray(existingGroup.users)) {
        if (typeof existingGroup.users[0] === 'object') {
          setSelectedUsers(existingGroup.users as User[]);
        }
      }
    }
  }, [isEditMode, existingGroup]);

  const handleInputChange = (field: keyof NotificationGroup, value: any) => {
    setGroup((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleUserSelection = (users: User[]) => {
    setSelectedUsers(users);
    setGroup((prev) => ({
      ...prev,
      users: users.map(user => user.id),
    }));
  };

  const handleAddFilter = () => {
    setQueryFilters([...queryFilters, { key: '', operator: 'eq', value: '', valueType: 'string' }]);
  };

  const handleRemoveFilter = (index: number) => {
    const updatedFilters = [...queryFilters];
    updatedFilters.splice(index, 1);
    setQueryFilters(updatedFilters);
    updateQueryFilterInGroup(updatedFilters);
  };

  const handleFilterChange = (index: number, field: keyof QueryFilterField, value: string | boolean | number) => {
    const updatedFilters = [...queryFilters];
    
    // If changing the key, update the value type
    if (field === 'key') {
      const valueType = getValueType(value as string, '');
      updatedFilters[index] = {
        ...updatedFilters[index],
        [field]: value as string,
        valueType,
        value: valueType === 'boolean' ? false : valueType === 'number' ? 0 : '',
      };
    } else {
      updatedFilters[index] = {
        ...updatedFilters[index],
        [field]: value,
      };
    }
    
    setQueryFilters(updatedFilters);
    updateQueryFilterInGroup(updatedFilters);
    
    // Clear preview count when filters change
    setPreviewCount(null);
  };

  const updateQueryFilterInGroup = (filters: QueryFilterField[]) => {
    const queryFilter: Record<string, any> = {};
    
    filters.forEach(filter => {
      if (!filter.key || !filter.operator) return;
      
      if (filter.operator === 'eq') {
        queryFilter[filter.key] = filter.value;
      } else {
        if (!queryFilter[filter.key]) {
          queryFilter[filter.key] = {};
        }
        queryFilter[filter.key][filter.operator] = filter.value;
      }
    });
    
    setGroup(prev => ({
      ...prev,
      query_filter: queryFilter
    }));
  };

  const handlePreviewQueryFilter = async () => {
    if (!group.query_filter || Object.keys(group.query_filter).length === 0) {
      return;
    }

    setIsPreviewing(true);
    try {
      const result = await previewQueryFilter({ query_filter: group.query_filter }).unwrap();
      setPreviewCount(result.total_users);
    } catch (error) {
      console.error('Preview failed:', error);
      setPreviewCount(0);
    } finally {
      setIsPreviewing(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!group.name) {
      newErrors.name = 'Name is required';
    }
    
    // Check if at least one user is selected or there's a query filter
    const hasUsers = group.users && group.users.length > 0;
    const hasQueryFilter = Object.keys(group.query_filter || {}).length > 0;
    
    if (!hasUsers && !hasQueryFilter) {
      newErrors.recipients = 'You must either select specific users or define query filters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await updateGroup({
          id: id as string,
          data: group,
        }).unwrap();
      } else {
        await createGroup(group).unwrap();
      }
      navigate('/connect');
    } catch (error) {
      console.error('Failed to save group:', error);
      setErrors({
        submit: 'Failed to save group. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/connect')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" component="h1">
          {isEditMode ? 'Edit Notification Group' : 'Create Notification Group'}
        </Typography>
      </Box>

      {errors.submit && (
        <Box sx={{ bgcolor: 'error.light', color: 'error.contrastText', p: 2, borderRadius: 1, mb: 3 }}>
          <Typography variant="body1">{errors.submit}</Typography>
        </Box>
      )}

      <Paper elevation={3}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Group Name"
                fullWidth
                required
                value={group.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name || ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                fullWidth
                value={group.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Users
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Manually select specific users to include in this notification group.
          </Typography>
          
          <UserAutocomplete
            value={selectedUsers}
            onChange={handleUserSelection}
            label="Select Users"
            placeholder="Search by name, username, or email"
          />
          
          {errors.recipients && !group.users?.length && !Object.keys(group.query_filter || {}).length && (
            <FormHelperText error>{errors.recipients}</FormHelperText>
          )}
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <div>
              <Typography variant="h6" gutterBottom>
                Query Filters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Define criteria to dynamically include users in this group.
              </Typography>
            </div>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddFilter}
            >
              Add Filter
            </Button>
          </Box>
          
          <Card variant="outlined">
            <CardContent>
              {queryFilters.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center">
                  No filters defined. Click 'Add Filter' to define criteria for including users.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {queryFilters.map((filter, index) => (
                    <Grid item xs={12} key={index}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                          label="Field"
                          value={filter.key}
                          onChange={(e) => handleFilterChange(index, 'key', e.target.value)}
                          size="small"
                          sx={{ minWidth: 150 }}
                          placeholder="e.g. team_id, country"
                        />
                        <TextField
                          select
                          label="Operator"
                          value={filter.operator}
                          onChange={(e) => handleFilterChange(index, 'operator', e.target.value)}
                          size="small"
                          sx={{ minWidth: 120 }}
                          SelectProps={{
                            native: true,
                          }}
                        >
                          <option value="eq">equals</option>
                          <option value="gt">greater than</option>
                          <option value="lt">less than</option>
                          <option value="gte">greater than or equal</option>
                          <option value="lte">less than or equal</option>
                          <option value="in">in</option>
                          <option value="contains">contains</option>
                        </TextField>
                        {filter.valueType === 'boolean' ? (
                          <TextField
                            select
                            label="Value"
                            value={filter.value}
                            onChange={(e) => handleFilterChange(index, 'value', e.target.value === 'true')}
                            size="small"
                            sx={{ minWidth: 120 }}
                            SelectProps={{ native: true }}
                          >
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </TextField>
                        ) : filter.valueType === 'number' ? (
                          <TextField
                            label="Value"
                            type="number"
                            value={filter.value}
                            onChange={(e) => handleFilterChange(index, 'value', parseFloat(e.target.value) || 0)}
                            size="small"
                            fullWidth
                            placeholder="Numeric value"
                          />
                        ) : filter.valueType === 'date' ? (
                          <TextField
                            label="Value"
                            type="datetime-local"
                            value={filter.value}
                            onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        ) : (
                          <TextField
                            label="Value"
                            value={filter.value}
                            onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                            size="small"
                            fullWidth
                            placeholder={filter.operator === 'in' ? 'Comma separated values' : 'Value'}
                          />
                        )}
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveFilter(index)}
                          edge="end"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Example filters: team_id equals 123, registration_date greater than 2023-01-01, country in "US,UK,CA"
          </Typography>
          
          {/* Preview Section */}
          {queryFilters.length > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handlePreviewQueryFilter}
                  disabled={isPreviewing}
                  size="small"
                >
                  {isPreviewing ? 'Previewing...' : 'Preview Results'}
                </Button>
                {previewCount !== null && (
                  <Typography variant="body2" color="primary">
                    <strong>{previewCount}</strong> users would match these filters
                  </Typography>
                )}
              </Box>
              {previewCount !== null && (
                <Typography variant="caption" color="text.secondary">
                  This count includes users matching the query filters plus any manually selected users.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Group'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default GroupForm;
